<script lang="ts" setup>
import { tpgIconDoNotDisturbOn } from '@tpg/grammar-web-icons';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarComboBoxOptions,
  GrammarMenuItemProps,
  TpgChangeEvent,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarComboBox,
} from '@nora-monorepo/grammar-ui';

import AvatarImage from '#ui-shared/components/avatar-image/AvatarImage.vue';
import type { UserWithLocalRole } from '#ui-shared/components/member-picker/member-picker-types';
import OptionsMenu from '#ui-shared/components/options-menu/OptionsMenu.vue';
import HoverableUserName from '#ui-shared/components/user-details/HoverableUserName.vue';
import { useRoleStore } from '#ui-shared/stores/role-store';
import { getInitials } from '#ui-shared/utils/get-initials';

interface MemberPickerUserItemProps {
  user: UserWithLocalRole;
  roleOptions: GrammarComboBoxOptions[];
}

const { user, roleOptions } = defineProps<MemberPickerUserItemProps>();

const emit = defineEmits<{
  updateUserRole: [userId: string, roleId: string];
  removeUser: [userId: string];
}>();

const { t } = useI18n();
const roleStore = useRoleStore();

function optionsMenuItems(member: UserWithLocalRole): GrammarMenuItemProps[] {
  if (member.nonRemovable) {
    return [];
  }

  return [
    {
      label: t('memberPicker.removeUser'),
      leadingIconTemplate: tpgIconDoNotDisturbOn,
      onSelect: () => {
        emit('removeUser', member.user.id);
      },
    },
  ];
}

function onComboBoxChange(event: TpgChangeEvent<string>): void {
  const selectedRoleId = event.value;
  if (selectedRoleId) {
    emit('updateUserRole', user.user.id, selectedRoleId);
  }
}

const computedLocalRole = computed(() => {
  const role = roleStore.availableOperationRoles?.find(
    role => role.id === user.role,
  );

  if (!role) {
    return undefined;
  }

  return role;
});
</script>

<template>
  <AvatarImage :id="user.user.id" class="mr-2" :text="getInitials(user.user)" />

  <HoverableUserName
    class="mx-1 flex-1"
    :user="user.user"
    :data-label="user.nonRemovable ? 'memberPicker.currentUser' : 'memberPicker.user'"
  />

  <div class="w-35 mr-3">
    <GrammarComboBox
      :label="t('operation.roleLabel')"
      :selected-value="computedLocalRole?.id || ''"
      :options="roleOptions"
      :full-width="true"
      :data-label="user.nonRemovable ? 'memberPicker.nonRemovableUserRole' : 'memberPicker.userRole'"
      @tpg-change="onComboBoxChange"
    />
  </div>
  <div class="self-center">
    <OptionsMenu v-if="!user.nonRemovable" :menu-items="optionsMenuItems(user)" />
    <span v-else data-label="memberPicker.nonRemovableUser" />
  </div>
</template>
